import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, useNotify } from '@routes-plan/ui';
import { ExpenseType } from '../../../models/expense-type';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, ContentCard, RowAction, RowActions, useConfirm } from '../shared/AdminUi';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface ExpenseTypeRow {
    id: number;
    name: string;
    category: string;
    operationType: string;
    status: string;
    active: boolean;
}

const ExpenseTypeList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    const [confirm, confirmDialog] = useConfirm();
    setPageTitle('expenseTypes.title');
    const [expenses, setExpenses] = useState<ExpenseType[]>([]);
    const [loading, setLoading] = useState(true);
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/expense-types');

    const load = async () => {
        const { data } = await axios.get('expense-types');
        setExpenses(Array.isArray(data?.data) ? data.data : []);
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await load();
            } catch (error) {
                notify.error(t('expenseTypes.loadError'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (row: ExpenseTypeRow) => {
        if (!canDelete) return;
        const ok = await confirm({
            title: t('expenseTypes.confirmDeleteTitle', { name: row.name }),
            message: t('expenseTypes.confirmDelete'),
        });
        if (!ok) return;
        try {
            const response = await axios.delete(`expense-types/${row.id}`);
            if (response.data?.code === 'EXPENSE_TYPE_DEACTIVATED') {
                // Tiene movimientos: queda inactivo para no perder el historial.
                await load();
                notify.info(t('expenseTypes.deactivatedOk'));
            } else {
                setExpenses((prev) => prev.filter((expense) => String(expense.id) !== String(row.id)));
                notify.success(t('common.deletedOk'));
            }
        } catch (error: any) {
            const code = error?.response?.data?.code;
            notify.error(code === 'EXPENSE_TYPE_SYSTEM' ? t('expenseTypes.systemCannotDelete') : t('common.deleteError'));
        }
    };

    const rows: ExpenseTypeRow[] = expenses.map((expense: ExpenseType) => ({
        id: expense.id,
        name: expense.name,
        category: expense.category?.name ?? '',
        operationType: expense.operationType?.name ?? '',
        status: expense.status?.name ?? '',
        active: Number(expense.status?.id) === 1,
    }));

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/expense-types/new"
            data-tour="expense-types-new"
        >
            {t('expenseTypes.newTitle')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('expenseTypes.title')} description={t('expenseTypes.subtitle')} actions={addButton} helpAction={<ScreenTourButton />} />
            <ContentCard>
                <DataTable<ExpenseTypeRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('expenseTypes.title')}
                    tourId="expense-types"
                    searchable
                    searchPlaceholder={t('expenseTypes.searchPlaceholder')}
                    exportFileName={t('expenseTypes.exportFileName')}
                    initialSort={{ columnId: 'name', direction: 'asc' }}
                    columns={[
                        { id: 'name', header: t('common.name'), value: (r) => r.name, sortable: true },
                        { id: 'category', header: t('expenseTypes.category'), value: (r) => r.category, sortable: true },
                        { id: 'operation', header: t('expenseTypes.operationType'), value: (r) => r.operationType, sortable: true, hideBelow: 'sm' },
                        {
                            id: 'status',
                            header: t('common.status'),
                            value: (r) => r.status,
                            render: (r) => <StatusChip label={r.status} tone={r.active ? 'success' : 'neutral'} />,
                            sortable: true,
                        },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            <RowAction
                                label={`${t('common.edit')} ${row.name}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/expense-types/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.name}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDelete(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<ReceiptLongOutlinedIcon />}
                            title={t('expenseTypes.empty')}
                            description={t('expenseTypes.emptyDescription')}
                            action={canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>
            {confirmDialog}
        </AdminPage>
    );
};

export default ExpenseTypeList;
